'use client';

import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { UserAccessModal, Tag, AccessInfoType } from '../../../common';
import {
  getReadOnlyTranslations,
  ReadOnlyTranslations,
} from '../../../layouts/utils/getReadOnlyTranslations';
import { UserAccessResult } from '../../utils/userAccess/utils';
import { useUserAccessControl } from './userAccessControl';
import { useModalDrawerContext } from '../../components/ModalDrawerContext/ModalDrawerContext';
import { RegularInfo } from '../../svgIcons';

const tagStatusStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'extra_small_4',
});

const getUserPermissionsStatusText = (
  accessResult: UserAccessResult,
  translations: ReadOnlyTranslations,
): string => {
  if (accessResult.accessInfoType === AccessInfoType.InfoApprovalPending) {
    return translations.pendingApproval;
  }

  if (accessResult.accessInfoType === AccessInfoType.WarningTrade) {
    return translations.tradingUnavailable;
  }

  return translations.readOnly;
};

export const ReadOnlyTag = () => {
  const { checkUserAccess } = useUserAccessControl();

  const accessResult = checkUserAccess();

  const { showModal } = useModalDrawerContext();

  const { t } = useClientTranslation();

  const translations = getReadOnlyTranslations(t as (key: string) => string);

  const userPermissionsStatusText = getUserPermissionsStatusText(
    accessResult,
    translations,
  );

  if (accessResult.allowed) {
    return null;
  }

  const handleClick = () => {
    if (!accessResult.allowed && accessResult.accessInfoType) {
      showModal(UserAccessModal, {
        type: accessResult.accessInfoType,
      });
    }
  };

  const isOutlineWarningVisual =
    accessResult.accessInfoType !== AccessInfoType.ReadOnly;

  return (
    <button
      className={css({ cursor: 'pointer' })}
      onClick={handleClick}
      aria-label="read-only account view details button"
      type="button"
    >
      <Tag
        visual={
          isOutlineWarningVisual ? 'outlineWarning' : 'outlineNeutralPrimary'
        }
        size="medium"
      >
        <div className={tagStatusStyles}>
          <RegularInfo size={16} />
          {userPermissionsStatusText}
        </div>
      </Tag>
    </button>
  );
};
